import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEmpresa } from '../../contexts/EmpresaContext';
import {
  LayoutDashboard, FileText, Building2, Users,
  Key, ScrollText, LogOut, User, X, ChevronRight, ChevronsUpDown,
  ShoppingCart, TrendingUp, Settings, Upload
} from 'lucide-react';
import clsx from 'clsx';

// Itens para admin e contador
const NAV_ADMIN = [
  { label: 'Dashboard',  icon: LayoutDashboard, path: '/dashboard',  perfis: ['admin','contador'] },
  { label: 'Documentos', icon: FileText,         path: '/documentos', perfis: ['admin','contador'] },
  { label: 'Empresas',   icon: Building2,        path: '/empresas',   perfis: ['admin','contador'] },
  { label: 'Usuários',   icon: Users,            path: '/usuarios',   perfis: ['admin'] },
  { label: 'API Keys',      icon: Key,           path: '/api-keys',      perfis: ['admin'] },
  { label: 'Importação',    icon: Upload,        path: '/importacao',    perfis: ['admin','contador'] },
  { label: 'Auditoria',     icon: ScrollText,    path: '/auditoria',     perfis: ['admin','contador'] },
  { label: 'Configurações', icon: Settings,      path: '/configuracoes', perfis: ['admin'] },
];

// Itens para cliente — separados por operação
const NAV_CLIENTE = [
  { label: 'Resumo',         icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Minhas Compras', icon: ShoppingCart,    path: '/documentos', query: 'operacao=recebimento' },
  { label: 'Minhas Vendas',  icon: TrendingUp,      path: '/documentos', query: 'operacao=emissao' },
  { label: 'Todos os Docs',  icon: FileText,        path: '/documentos' },
  { label: 'Minha Empresa',  icon: Building2,       path: '/minha-empresa' },
  { label: 'Importação',     icon: Upload,          path: '/importacao' },
];

export default function Sidebar({ aberta, onFechar }) {
  const { usuario, logout } = useAuth();
  const { empresas, empresaAtual, setEmpresaAtual, temMultiplasEmpresas } = useEmpresa() || {};
  const navigate  = useNavigate();
  const { pathname, search } = useLocation();

  const isCliente = usuario?.perfil === 'cliente';

  const itens = isCliente
    ? NAV_CLIENTE
    : NAV_ADMIN.filter(i => i.perfis.includes(usuario?.perfil));

  function ativo(item) {
    if (item.path !== pathname) return false;
    if (item.query) return search.includes(item.query);
    if (isCliente && item.path === '/documentos') return !search; // "Todos" só ativo sem query
    return true;
  }

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <aside className={clsx(
      'fixed lg:relative z-30 h-full flex flex-col transition-all duration-300',
      'bg-[#0a0f1e] border-r border-surface-border w-64',
      aberta ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
    )}>

      {/* Logo */}
      <div className="flex items-center justify-between px-5 py-5 border-b border-surface-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-lg">
            <FileText size={18} className="text-white" />
          </div>
          <div>
            <p className="font-bold text-white text-sm leading-tight">Kairós</p>
            <p className="text-xs text-slate-500 leading-tight">Storage Fiscal</p>
          </div>
        </div>
        <button onClick={onFechar} className="lg:hidden text-slate-500 hover:text-white p-1">
          <X size={18} />
        </button>
      </div>

      {/* Badge de perfil */}
      <div className="px-4 py-3 border-b border-surface-border">
        <div className="flex items-center gap-2 bg-surface-card rounded-lg px-3 py-2">
          <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center text-xs font-bold text-white">
            {usuario?.nome?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-200 truncate">{usuario?.nome}</p>
            <p className="text-[10px] text-slate-500 capitalize">{usuario?.perfil}</p>
          </div>
        </div>
      </div>

      {/* Seletor de empresa — so aparece pra grupo economico com mais de uma empresa vinculada (ticket 907) */}
      {temMultiplasEmpresas && (
        <div className="px-4 pt-3 pb-1">
          <label className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider px-1">Empresa</label>
          <div className="relative mt-1">
            <select
              value={empresaAtual?.id || ''}
              onChange={e => setEmpresaAtual(empresas.find(emp => emp.id === e.target.value))}
              className="w-full appearance-none bg-surface-card border border-surface-border rounded-lg pl-3 pr-8 py-2 text-xs font-medium text-slate-200 truncate cursor-pointer hover:border-primary-600/50 transition-colors"
            >
              {empresas.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.razao_social}</option>
              ))}
            </select>
            <ChevronsUpDown size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          </div>
        </div>
      )}

      {/* Navegação */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider px-3 pb-2">
          {isCliente ? 'Meus Documentos' : 'Menu'}
        </p>
        {itens.map(item => {
          const href = item.query ? `${item.path}?${item.query}` : item.path;
          const itemAtivo = ativo(item);

          return (
            <NavLink
              key={href}
              to={href}
              onClick={() => window.innerWidth < 1024 && onFechar()}
              className={clsx(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group',
                itemAtivo
                  ? 'bg-primary-600/20 text-primary-400 border border-primary-600/30'
                  : 'text-slate-400 hover:bg-surface-card hover:text-slate-200'
              )}
            >
              <item.icon size={16} className={itemAtivo ? 'text-primary-400' : 'text-slate-500 group-hover:text-slate-300'} />
              <span className="flex-1">{item.label}</span>
              {itemAtivo && <ChevronRight size={12} className="text-primary-500" />}
            </NavLink>
          );
        })}
      </nav>

      {/* Rodapé */}
      <div className="px-3 pb-4 space-y-0.5 border-t border-surface-border pt-3">
        <NavLink
          to="/perfil"
          className={({ isActive }) => clsx(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
            isActive ? 'bg-primary-600/20 text-primary-400' : 'text-slate-400 hover:bg-surface-card hover:text-slate-200'
          )}
        >
          <User size={16} className="text-slate-500" />
          <span>Meu Perfil</span>
        </NavLink>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:bg-red-900/20 hover:text-red-400 transition-all duration-150"
        >
          <LogOut size={16} />
          <span>Sair</span>
        </button>
      </div>
    </aside>
  );
}
